import React, { useEffect, useRef, useState } from 'react';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { ChatFilterType, ChatModel, ChatSort, GetChatActivityResponse } from '../../../../api-service/clients';
import { usePolling } from '../../../../hooks/use-polling';
import { useCanWriteCitizenDialog } from '../citizen-dialog-access';
import { ForumNewChatDialog } from './forum-new-chat-dialog';
import { ChatStatus, ForumChatsTabComponent, ICaseworkerOption } from './forum-chats-tab-component';

const client = moduleClientFactory.createForumCaseworkerClient();
const activityPollIntervalMs = 60000;

export interface IForumChatsTabProps {
  onlyMine?: boolean;
}

interface IChatsQuery {
  caseworkerId: string;
  unreadOnly: boolean;
  status: ChatStatus;
  chatSort: ChatSort;
  page: number;
  pageSize: number;
}

const defaultChatsQuery: IChatsQuery = {
  caseworkerId: '',
  unreadOnly: false,
  status: 'open',
  chatSort: ChatSort.LatestActivityDate,
  page: 0,
  pageSize: 25,
};

export function ForumChatsTab(props: IForumChatsTabProps) {
  const { onlyMine } = props;
  const canWriteCitizenDialog = useCanWriteCitizenDialog();

  const [chats, setChats] = useState<ChatModel[]>([]);
  const [chatCount, setChatCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFailed, setHasFailed] = useState(false);
  const [caseworkers, setCaseworkers] = useState<ICaseworkerOption[]>([]);
  const [query, setQuery] = useState<IChatsQuery>(defaultChatsQuery);
  const [listedActivity, setListedActivity] = useState<GetChatActivityResponse>();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const latestRequest = useRef(0);

  useEffect(() => {
    loadChats(query);
  }, [onlyMine, query]);

  usePolling(reloadChatsWhenActivityChanged, activityPollIntervalMs, !isLoading && !hasFailed);

  async function reloadChatsWhenActivityChanged() {
    const requestBeforePoll = latestRequest.current;
    const activity = await client.getChatActivity(listFilterFor(!!onlyMine, query)).catch(() => undefined);
    if (activity && latestRequest.current === requestBeforePoll && !isSameActivity(activity, listedActivity)) {
      await loadChats(query, { inBackground: true });
    }
  }

  useEffect(() => {
    if (onlyMine) {
      return;
    }
    client
      .getForumCaseworkers()
      .then(result =>
        setCaseworkers((result.caseworkers ?? []).map(caseworker => ({ id: caseworker.id, name: caseworker.name ?? '' })).sort((a, b) => a.name.localeCompare(b.name, 'da')))
      )
      .catch(() => setCaseworkers([]));
  }, [onlyMine]);

  async function loadChats(chatsQuery: IChatsQuery, { inBackground } = { inBackground: false }) {
    const request = ++latestRequest.current;
    if (!inBackground) {
      setIsLoading(true);
      setHasFailed(false);
    }
    const { chatFilter, impersonatedCaseworkerId } = listFilterFor(!!onlyMine, chatsQuery);
    try {
      const [result, count, activity] = await Promise.all([
        client.getChatsPaginated({
          pageLimit: chatsQuery.pageSize,
          currentPage: chatsQuery.page + 1,
          chatSort: chatsQuery.chatSort,
          chatFilter,
          impersonatedCaseworkerId,
        }),
        client.getChatCount({ chatFilter, impersonatedCaseworkerId }).catch(() => undefined),
        client.getChatActivity({ chatFilter, impersonatedCaseworkerId }).catch(() => undefined),
      ]);
      if (request === latestRequest.current) {
        const loadedChats = result.chats ?? [];
        setChats(loadedChats);
        setListedActivity(activity);
        const chatsUpToThisPage = chatsQuery.page * chatsQuery.pageSize + loadedChats.length;
        setChatCount(count?.count ?? chatsUpToThisPage);
      }
    } catch {
      // A failed background refresh leaves the list as it was; the next poll tries again.
      if (request === latestRequest.current && !inBackground) {
        // Without this the grid would claim the caseworker has no messages when the call failed.
        setHasFailed(true);
        setChats([]);
        setChatCount(0);
      }
    } finally {
      if (request === latestRequest.current) {
        setIsLoading(false);
      }
    }
  }

  function changeFilter(change: Partial<Omit<IChatsQuery, 'page'>>) {
    setQuery(current => (Object.entries(change).every(([key, value]) => current[key as keyof IChatsQuery] === value) ? current : { ...current, ...change, page: 0 }));
  }

  function changePage(page: number) {
    setQuery(current => ({ ...current, page }));
  }

  function handleStatusChange(status: ChatStatus) {
    changeFilter({ status });
  }

  function handleChatSortChange(chatSort: ChatSort) {
    changeFilter({ chatSort });
  }

  function handleCaseworkerChange(caseworkerId: string) {
    changeFilter({ caseworkerId });
  }

  function handleUnreadOnlyChange(unreadOnly: boolean) {
    changeFilter({ unreadOnly });
  }

  function handlePageSizeChange(pageSize: number) {
    changeFilter({ pageSize });
  }

  function handleChatClick(chat: ChatModel) {
    // A confidential chat the caseworker is not part of must not open; the same rule guards the citizen accordion.
    if (chat.isConfidential && !chat.isPartOf) {
      return;
    }

    const fasitCitizenId = chat.citizen?.externalId;
    if (!fasitCitizenId) {
      return;
    }

    window.open(`/citizen/${fasitCitizenId}/chat/${chat.id}`);
  }

  function handleChatCreated() {
    setIsCreateOpen(false);
    loadChats(query);
  }

  return (
    <>
      <ForumChatsTabComponent
        chats={chats}
        caseworkers={caseworkers}
        isLoading={isLoading}
        hasFailed={hasFailed}
        status={query.status}
        onStatusChange={handleStatusChange}
        chatSort={query.chatSort}
        onChatSortChange={handleChatSortChange}
        caseworkerId={query.caseworkerId}
        onCaseworkerChange={handleCaseworkerChange}
        unreadOnly={query.unreadOnly}
        onUnreadOnlyChange={handleUnreadOnlyChange}
        onChatClick={handleChatClick}
        showCaseworkerFilter={!onlyMine}
        page={query.page}
        pageSize={query.pageSize}
        rowCount={chatCount}
        onPageChange={changePage}
        onPageSizeChange={handlePageSizeChange}
        onRefresh={() => loadChats(query)}
        onCreateChat={canWriteCitizenDialog ? () => setIsCreateOpen(true) : undefined}
      />
      <ForumNewChatDialog open={isCreateOpen} onCreated={handleChatCreated} onCancel={() => setIsCreateOpen(false)} />
    </>
  );
}

function listFilterFor(onlyMine: boolean, query: IChatsQuery) {
  return { chatFilter: chatFilterFor(onlyMine, query), impersonatedCaseworkerId: query.caseworkerId || undefined };
}

function isSameActivity(activity: GetChatActivityResponse, listedActivity?: GetChatActivityResponse) {
  return activity.count === listedActivity?.count && timeOf(activity.latestActivityDate) === timeOf(listedActivity?.latestActivityDate);
}

function timeOf(date?: Date | string) {
  return date ? new Date(date).getTime() : undefined;
}

function chatFilterFor(onlyMine: boolean, query: IChatsQuery) {
  const filter: ChatFilterType[] = [];
  if (onlyMine) {
    filter.push(ChatFilterType.PartOf);
  }
  filter.push(query.status === 'open' ? ChatFilterType.Active : ChatFilterType.Inactive);
  if (query.unreadOnly) {
    filter.push(ChatFilterType.ContainsUnreadMessages);
  }
  return filter;
}
